/**
 * Typed client for the SAFE assessment API (`api/db/safe/*`).
 *
 * The one thing to understand before using this: **the session endpoint is stateless.** The
 * server holds no assessment. The client owns the whole workshop document, posts it, and gets
 * a computed view of it back — scores, coverage, badges, flow settings, export payload. Nothing
 * is persisted server-side, which is why every call here sends `workshop` and why the response
 * always echoes a `workshop` the caller is expected to store (see `safeWorkshop.ts`).
 *
 * That design is inherited from the framework tool, whose UI kept the assessment in browser
 * storage and computed scores in two web workers (`capability-worker.js`, `risk-worker.js`).
 * `db/services/safe_workflows.py` is a faithful port of those workers, so this app does the
 * same arithmetic by asking the server instead of shipping the workers — one implementation,
 * and the scoring cannot drift between the UI and the Go port that also consumes it.
 *
 * Two consequences worth stating plainly:
 *
 *  - **Edits are commands, not state.** You do not PATCH the workshop; you post the workshop
 *    plus an ordered list of `edits`, and the server returns the result of applying them. Order
 *    matters: `set_group_score` clears every per-threat override in its group, exactly as the
 *    original UI did, so a group score followed by a threat score is not the same as the
 *    reverse.
 *  - **Ask for what you need.** `include` selects sections of the response. The defaults are
 *    `summary`, `capabilities` and `workshop`; `badges`, `flow`, `pages`, `export`,
 *    `collections` and `architecture` are computed only on request, and `badges`/`flow` are the
 *    expensive ones.
 *
 * Every id in an edit is validated server-side against the collections, so an unknown
 * capability or BCT is a 400 rather than a silently ignored edit.
 */
import { buildApiUrl, getApiHeaders } from '../queriesHelpers';

/* ------------------------------------------------------------------ collections */

/** Which of the five SAFE collections. These names are the URL segment, verbatim. */
export type SafeCollectionName =
  | 'bustech'
  | 'risk'
  | 'capability'
  | 'pin'
  | 'securityfunction';

export const SAFE_COLLECTION_NAMES: SafeCollectionName[] = [
  'bustech',
  'risk',
  'capability',
  'pin',
  'securityfunction',
];

/**
 * A business-critical technology.
 *
 * The odd field names (`bustechid`, `bustechdes`) are the framework tool's column names kept
 * as-is through the merge, because the assessment export format uses them and renaming them
 * here would break import of a file exported by the original tool.
 */
export type SafeBusTech = {
  bustechid: number;
  name: string;
  bustechdes?: string | null;
  bustechimpact?: string | null;
  bustechsegment?: string | null;
  bustechupdate?: string | null;
  targets?: string | null;
  update?: string | null;
  type: 'bustech';
  risk: { impact: string; name: string; riskid: number; severity?: number | null }[];
  /** Industry flags arrive as 0/1 ints, one key per industry. */
  [industryFlag: string]: any;
};

export type SafeRisk = {
  id: number;
  name: string;
  /** Impact *name*, not id — `HALF_SCORE_IMPACTS` matches on the name. */
  impact: string;
  update?: string | null;
  type: 'risk';
  bustech: { id: number; name: string }[];
  capability: { domain: string; id: number; name: string }[];
};

export type SafeCapability = {
  id: number;
  name: string;
  /** Domain *name*; the capability table groups by it. */
  domain: string;
  image?: string | null;
  centralized?: any;
  maturitylevel?: number | null;
  update?: string | null;
  type: 'capability';
  risk: { id: number; impact: string; name: string }[];
  pin: { id: number; name: string }[];
  product: { id: number; name: string }[];
  securityfunction: { id: number; name: string }[];
};

export type SafePin = {
  id: number;
  name: string;
  order?: number | null;
  update?: string | null;
  type: 'pin';
  capability: { name: string }[];
};

/** Note the space in `type`: it is 'security function', not the collection slug. */
export type SafeSecurityFunction = {
  id: number;
  name: string;
  type: 'security function';
};

export type SafeCollections = {
  bustech: SafeBusTech[];
  risk: SafeRisk[];
  capability: SafeCapability[];
  pin: SafePin[];
  securityfunction: SafeSecurityFunction[];
};

export type SafeCollectionsResponse = SafeCollections & {
  counts: Record<string, number>;
  includeHidden: boolean;
};

/* --------------------------------------------------------------------- session */

export type SafeMode = 'risk' | 'architect';

export type SafeInclude =
  | 'summary'
  | 'capabilities'
  | 'badges'
  | 'pages'
  | 'flow'
  | 'workshop'
  | 'export'
  | 'collections'
  | 'architecture';

/**
 * A workshop document: one key per selection or setting store.
 *
 * Left deliberately loose. The server owns the schema and round-trips unknown keys, so typing
 * every store here would mean this file has to be edited whenever a store gains a field —
 * and a stale type would reject a workshop the server is perfectly happy with.
 */
export type SafeWorkshop = Record<string, any>;

/**
 * The store names a workshop carries. Selection stores are id-keyed; setting stores are flat.
 *
 * Keys are stringified in transit, because that is how the browser stored them and the export
 * format follows suit — so anything reading a store by id has to stringify first. `safeStore`
 * below exists so that is written once.
 */
export type SafeSelectionStore =
  | 'capability'
  | 'bct_risk'
  | 'maturity'
  | 'security_function'
  | 'custom_capability'
  | 'pin_filter'
  | 'fw_product';

/** One threat as it hangs off a BCT. `score` is the per-threat override; null means "use group". */
export type SafeThreatSettings = {
  id?: number;
  name: string;
  active: boolean;
  score: number | null;
  group: string;
};

/** One impact group's severity on one BCT. Threat overrides in the group defer to this. */
export type SafeGroupSettings = {
  id?: number;
  active: boolean;
  score: number | null;
};

/**
 * One BCT in the workshop.
 *
 * `type` is 'Core' for a technology that applies to more than eight industries, 'Special' for the
 * rest, and 'Specific' for one the assessor added by hand. Only 'Core' starts selected, which is
 * why an untouched Risk assessment already has something to score.
 */
export type SafeBctSettings = {
  id: number;
  name: string;
  active?: boolean;
  /** Active threat count, maintained server-side. The badge in the BCT list. */
  count?: number;
  type: 'Core' | 'Special' | 'Specific' | string;
  /** Base priority, 0-100. Multiplied by the threat severity to give the Final column. */
  score?: number;
  risks: Record<string, SafeThreatSettings>;
  groups: Record<string, SafeGroupSettings>;
  description?: string | null;
};

/** One product offered against a capability. `type` is 'cisco' or '3rd-party'. */
export type SafeProductSettings = {
  name: string;
  active?: boolean;
  type?: string;
};

export type SafeCapabilitySettings = {
  id: number;
  name: string;
  active?: boolean;
  status?: SafeCapabilityStatus;
  /** Risk-mode priority, written by the capability worker. 0 in architect mode. */
  score?: number;
  image?: string | null;
  products?: SafeProductSettings[];
  /** The planned state, drawn by the 'proposed' flow diagram. */
  proposed?: SafeProductSettings[];
};

/** A pin, maturity level or security function filter row. */
export type SafeFilterSettings = {
  id: number;
  name: string;
  active?: boolean;
  [key: string]: any;
};

/**
 * Read one selection store off a workshop, id-keyed as strings.
 *
 * Returns an empty object rather than undefined for a store the server has not sent, so callers
 * can iterate without guarding. A missing store is not an error: the session endpoint only
 * returns `workshop` when it is in `include`.
 */
export function safeStore<T = any>(
  workshop: SafeWorkshop | null | undefined,
  name: SafeSelectionStore,
): Record<string, T> {
  const store = workshop?.[name];

  return store && typeof store === 'object' ? (store as Record<string, T>) : {};
}

/** Read one settings store off a workshop (`safe_settings`, `capability_groups`, ...). */
export function safeSettings(
  workshop: SafeWorkshop | null | undefined,
  name: 'capability_groups' | 'bct_categories' | 'app_settings' | 'third_party_products' | 'safe_settings',
): Record<string, any> {
  const store = workshop?.[name];

  return store && typeof store === 'object' ? (store as Record<string, any>) : {};
}

/**
 * The 16 edit operations. `op` is required; which other fields are required depends on the op,
 * and the server rejects a malformed edit with a 400 naming the missing field.
 */
export type SafeEdit =
  | { op: 'select_bct'; bct: number }
  | { op: 'deselect_bct'; bct: number }
  | { op: 'set_bct_score'; bct: number; score: number }
  | { op: 'toggle_risk'; bct: number; risk: number }
  | { op: 'set_risk_active'; bct: number; risk: number; active: boolean }
  | { op: 'set_risk_score'; bct: number; risk: number; score: number }
  | { op: 'set_group_score'; bct: number; group: string; score: number }
  // `id` is required rather than generated: the endpoint refuses an id it already knows, and a
  // client that picks the id can address the BCT it just created in the same request.
  | { op: 'add_bct'; id: number; name: string; description?: string; score?: number }
  | { op: 'set_capability_status'; capability: number; status: SafeCapabilityStatus }
  | {
      op: 'set_product_active';
      capability: number;
      product: string;
      active: boolean;
      prop?: 'products' | 'proposed';
    }
  | {
      op: 'add_third_party_product';
      capability: number;
      product: string;
      prop?: 'products' | 'proposed';
    }
  | { op: 'add_custom_capability'; id: number; values: Record<string, any> }
  // The three filter stores take `id`, not a store-specific field name — they are one server-side
  // helper parameterised by store, so the payload shape is identical for all three.
  | { op: 'set_pin_active'; id: number; active: boolean }
  | { op: 'set_maturity_active'; id: number; active: boolean }
  | { op: 'set_security_function_active'; id: number; active: boolean }
  | { op: 'configure'; settings: Record<string, any> }
  // Escape hatch for an op added server-side before this union catches up. Typing the union
  // shut would make the client the thing that has to be redeployed first.
  | ({ op: string } & Record<string, any>);

export type SafeCapabilityStatus = 'None' | 'Planned' | 'Partial' | 'Full' | 'Hide';

export type SafeCapabilityRow = {
  id: number;
  name: string;
  domain: string;
  /** Risk-mode score, or the hard-coded architect priority. Lower sorts later. */
  priority: number;
  status: SafeCapabilityStatus;
  products: string[];
  proposed: string[];
  /** True when the chosen coverage claims a product but none is selected. */
  needsProduct: boolean;
};

export type SafeSummary = {
  safeMode: SafeMode;
  pinsReady: boolean;
  enoughRisk: boolean;
  bcts: number;
  bctsSelected: number;
  threatsSelected: number;
  capabilities: number;
  capabilityMaxScore: number;
  coverage: Record<string, number>;
  thirdPartyProducts: string[];
  customCapabilities: number;
};

export type SafeFlowPayload = {
  flowType: string;
  showProducts: boolean;
  showAllPins: boolean;
  maxCapabilitiesPerPin: number;
  pathColor: string;
  startPointImage: string;
  startPoint: string;
  endPointImage: string;
  endPoint: string;
  capabilityMaxScore: number;
  width: number;
  height: number;
  /** floor(50 / activePins): the per-pin cap the pin budget allows. */
  maxCapabilitiesPerPinLimit: number;
};

export type SafeBadge = {
  name: string;
  image: string;
  border: string;
  /**
   * The active products behind this badge, as full records rather than names.
   *
   * `capability_rows` reduces products to names for the tables; `select_badges` does not, because
   * the diagram draws a product box whose colour depends on `type`.
   */
  products: SafeProductSettings[];
};

/** One entry per active pin, keyed by pin name. */
export type SafeBadges = Record<string, SafeBadge[]>;

export type SafeBctPageThreat = {
  name: string;
  score: number;
  [key: string]: any;
};

export type SafeBctPageEntry = {
  name: string;
  description: string;
  score: number;
  threats: SafeBctPageThreat[];
};

export type SafeExport = {
  filename: string;
  assessment: SafeAssessmentDocument;
};

/** The export/import file format. String keys, three required sections. */
export type SafeAssessmentDocument = {
  bct: Record<string, any>;
  capability: Record<string, any>;
  customProduct: Record<string, any>;
  [key: string]: any;
};

export type SafeSessionResponse = {
  mode: SafeMode;
  ready: { pins: boolean; risk: boolean };
  capabilityMaxScore: number;
  warnings: string[];
  summary?: SafeSummary;
  capabilities?: SafeCapabilityRow[];
  flow?: SafeFlowPayload;
  badges?: SafeBadges;
  pages?: SafeBctPageEntry[][];
  export?: SafeExport;
  architecture?: { id: number; name: string; capability: { id: number; name: string }[] }[];
  collections?: SafeCollections;
  workshop?: SafeWorkshop;
};

export type SafeSessionRequest = {
  mode?: SafeMode;
  workshop?: SafeWorkshop | null;
  settings?: Record<string, any>;
  edits?: SafeEdit[];
  include?: SafeInclude[];
  includeHidden?: boolean;
};

/* ------------------------------------------------------- priority / diagnostics */

export type SafeCapabilityPriority = {
  /** 72 hard-coded architect priorities, keyed as the server emits them. */
  priorities: any;
  count: number;
  /** A capability absent from `priorities` scores this, which sorts it *first*. */
  unlistedPriority: number;
  architectScoreMax: number;
  statuses: SafeCapabilityStatus[];
  halfScoreImpacts: string[];
  specialPins: string[];
  flowTypes: string[];
  pathColors: string[];
  gapColors: Record<string, string>;
  endIcons: string[];
};

export type SafeDiagnostics = {
  counts: Record<string, number>;
  unresolved: Record<string, any>;
  /** False when the data would score wrong (e.g. `risk.impact` still holds ids). */
  resolvable: boolean;
};

export type SafeValidationResult = {
  valid: boolean;
  requiredKeys: string[];
  summary?: Record<string, any>;
};

export type SafeImportResult = SafeSessionResponse & {
  import: {
    /** Keys present in the file that no longer match anything in the database. */
    missing?: Record<string, any>;
    [key: string]: any;
  };
};

/* ----------------------------------------------------------------------- fetch */

const SAFE_BASE = 'db/safe';

const errorMessage = (payload: any, status: number, fallback: string): string => {
  const detail = payload?.detail ?? payload?.error ?? payload?.message;

  if (Array.isArray(detail) && detail.length) return String(detail[0]);
  if (detail) return String(detail);
  if (typeof payload === 'string' && payload.trim()) return payload;

  return `${fallback} (${status})`;
};

/**
 * All SAFE requests go through here so they carry auth.
 *
 * `getApiHeaders` is not optional: a bare `fetch` at a relative `/api` path drops the toolbox
 * token in local development, where the app and the API are different origins, and the request
 * comes back 401 with no obvious cause.
 */
async function safeApiFetch<T = any>(
  method: 'GET' | 'POST',
  endpoint: string,
  options: { params?: Record<string, any>; body?: any } = {},
): Promise<T> {
  const { params = {}, body } = options;

  const headers = { ...getApiHeaders(null, method, endpoint) } as Record<string, string>;

  const response = await fetch(buildApiUrl(endpoint, params, true), {
    method,
    headers,
    credentials: 'include',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await response.text();

  let parsed: any = null;

  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!response.ok) {
    const error: any = new Error(errorMessage(parsed, response.status, 'SAFE request failed'));
    error.status = response.status;
    error.payload = parsed;
    throw error;
  }

  return parsed as T;
}

/* ------------------------------------------------------------------- endpoints */

/**
 * All five collections in one round trip, plus row counts.
 *
 * `includeHidden` defaults to true server-side, matching the framework tool: hidden rows are
 * included unless asked otherwise. Passing false here is how an assessment excludes them.
 */
export async function getSafeCollections(
  options: { includeHidden?: boolean } = {},
): Promise<SafeCollectionsResponse> {
  const params: Record<string, any> = {};
  if (options.includeHidden !== undefined) params.includeHidden = options.includeHidden;

  return await safeApiFetch<SafeCollectionsResponse>('GET', `${SAFE_BASE}/collections/`, {
    params,
  });
}

/** One collection as a bare array. Prefer `getSafeCollections` unless you need exactly one. */
export async function getSafeCollection<T = any>(
  name: SafeCollectionName,
  options: { includeHidden?: boolean } = {},
): Promise<T[]> {
  const params: Record<string, any> = {};
  if (options.includeHidden !== undefined) params.includeHidden = options.includeHidden;

  return await safeApiFetch<T[]>('GET', `${SAFE_BASE}/collection/${name}/`, { params });
}

/**
 * The architect-mode priority table and the enumerations that go with it.
 *
 * Fetched rather than duplicated in the frontend: the table is 72 hand-curated entries and the
 * "unlisted sorts first" rule is invisible from the outside, so a local copy would drift.
 */
export async function getSafeCapabilityPriority(): Promise<SafeCapabilityPriority> {
  return await safeApiFetch<SafeCapabilityPriority>(
    'GET',
    `${SAFE_BASE}/capability-priority/`,
  );
}

/**
 * Whether this database can answer an assessment correctly.
 *
 * Worth surfacing in the admin section: a toolbox with no `business_technology` rows serves a
 * syntactically perfect, entirely empty assessment, and one whose `risk.impact` holds ids
 * scores every impact group at 75 instead of 50. Neither raises.
 */
export async function getSafeDiagnostics(): Promise<SafeDiagnostics> {
  return await safeApiFetch<SafeDiagnostics>('GET', `${SAFE_BASE}/diagnostics/`);
}

/**
 * Apply edits to a workshop and get the computed assessment back.
 *
 * Send `workshop: null` (or omit it) to start from defaults: Core BCTs selected, every
 * capability at 'None'. Always store the returned `workshop` — it is the document, and the
 * next call has to send it back.
 */
export async function postSafeSession(
  request: SafeSessionRequest,
): Promise<SafeSessionResponse> {
  return await safeApiFetch<SafeSessionResponse>('POST', `${SAFE_BASE}/session/`, {
    body: request,
  });
}

/** Check an assessment file before importing it, so a bad file fails before it replaces state. */
export async function validateSafeAssessment(
  assessment: any,
): Promise<SafeValidationResult> {
  return await safeApiFetch<SafeValidationResult>(
    'POST',
    `${SAFE_BASE}/assessment/validate/`,
    { body: { assessment } },
  );
}

/**
 * Import an assessment file and get the resulting session in the same response.
 *
 * `import.missing` names anything in the file that no longer exists in the database — a
 * capability that was deleted, a BCT that was renamed. The import still succeeds; those
 * entries are dropped, which is why the result is worth showing rather than discarding.
 */
export async function importSafeAssessment(
  assessment: any,
  options: { mode?: SafeMode; include?: SafeInclude[] } = {},
): Promise<SafeImportResult> {
  const body: Record<string, any> = { assessment };

  if (options.mode) body.mode = options.mode;
  if (options.include) body.include = options.include;

  return await safeApiFetch<SafeImportResult>('POST', `${SAFE_BASE}/assessment/import/`, {
    body,
  });
}
